import type { ActionFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import { zfd } from 'zod-form-data';
import * as z from 'zod';
import { gql } from '@apollo/client';
import { UserErrorResponse } from '@modules/root/graphql/responses';

// eslint-disable-next-line import/no-unused-modules
export const UPSCALE_COMPARISON_API_ROUTE =
    '/remix-api/page-builder/upscale-comparison';

const comparisonValidation = {
    libraryImageId: zfd.numeric(z.number().positive()),
    currentImageUrl: zfd.text(z.string().min(1)),
};

const GET_UPSCALE_ORIGINAL_IMAGE_URL = gql`
    query GetUpscaleOriginalImageUrl(
        $LibraryImageID: Int!
        $CurrentImageUrl: String!
    ) {
        getUpscaleOriginalImageUrl(
            LibraryImageID: $LibraryImageID
            CurrentImageUrl: $CurrentImageUrl
        ) {
            originalImageUrl
            originalLibraryImageId
            upscaledLibraryImageId
            originalWidth
            originalHeight
        }
    }
`;

export type PageBuilderUpscaleComparisonAction = typeof action;

/**
 * Fetch the original (pre-upscale) image URL for an already-upscaled image.
 *
 * The backend resolver swaps the upscaled filename in the current image service
 * URL with the original filename, preserving all crop/zoom/pan/filter parameters.
 * This guarantees the before/after comparison has identical framing.
 */
// eslint-disable-next-line import/no-unused-modules
export const action: ActionFunction = async ({ request, context }) => {
    const validation = zfd.formData(comparisonValidation);
    const result = validation.safeParse(await request.formData());

    if (!result.success) {
        throw new UserErrorResponse(result.error);
    }

    const { libraryImageId, currentImageUrl } = result.data;

    try {
        const { data, errors } = await context.client.query({
            query: GET_UPSCALE_ORIGINAL_IMAGE_URL,
            variables: {
                LibraryImageID: libraryImageId,
                CurrentImageUrl: currentImageUrl,
            },
            fetchPolicy: 'no-cache',
        });

        if (errors) {
            throw new Error(errors[0]?.message || 'GraphQL error occurred');
        }

        const queryResult = data?.getUpscaleOriginalImageUrl;

        if (!queryResult?.originalImageUrl) {
            throw new Error('Original image not found for this upscaled image');
        }

        return json({
            success: true,
            originalImageUrl: queryResult.originalImageUrl as string,
            originalLibraryImageId:
                queryResult.originalLibraryImageId as number,
            upscaledLibraryImageId:
                queryResult.upscaledLibraryImageId as number,
            originalWidth: queryResult.originalWidth as number,
            originalHeight: queryResult.originalHeight as number,
        });
    } catch (error) {
        return json(
            {
                success: false,
                error:
                    error instanceof Error
                        ? error.message
                        : 'Failed to fetch comparison data',
            },
            { status: 500 },
        );
    }
};
